import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { loadVegaEmbed } from "@/lib/viz/cdnLoader";
import { prepareVegaLiteSpec } from "@/lib/viz/dataSubstitution";
import type { VizRenderPayload } from "@/lib/viz/types";

interface Props {
  payload: VizRenderPayload;
}

/**
 * Vega-Lite renderer for operate-ui. Loads vega-embed on demand and
 * delegates rendering to it; surfaces errors inline so a broken spec
 * doesn't crash the chat thread.
 */
export const VegaLiteRenderer: React.FC<Props> = ({ payload }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let result: { view?: { finalize?: () => void } } | null = null;

    (async () => {
      try {
        const embed = await loadVegaEmbed();
        if (cancelled || !containerRef.current) return;
        const spec = prepareVegaLiteSpec(payload);
        result = await embed(containerRef.current, spec, {
          actions: false,
          renderer: "svg",
        });
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "unknown error";
          setError(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { result?.view?.finalize?.(); } catch { /* no-op */ }
    };
  }, [payload]);

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
        <div className="font-semibold">Vega-Lite chart could not render</div>
        <div className="mt-1 text-xs text-rose-700">{error}</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full min-h-[280px] max-h-[60vh] overflow-hidden"
    />
  );
};
